import Box from '@mui/material/Box';
import InputCheckbox from 'common/components/Elements/Form/InputCheckbox/InputCheckbox';
import InputCheckboxGroup from 'common/components/Elements/Form/InputCheckboxGroup/InputCheckboxGroup';
import ModalActions from 'common/components/Elements/Modal/ModalActions/ModalActions';
import SimpleModal, { ScrollableModalContent } from 'common/components/Elements/Modal/SimpleModal/SimpleModal';
import { arrayToggle } from 'common/utilities/core/array';
import type { AppDispatch } from 'core/redux/store/configureStore.types';
import I18n from 'i18n/en-US';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import type { EntitySequence as Sequence,SequenceAlign } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import type { HandleAlignedSequencesChange } from 'sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser';
import { removedSequencesInserted } from 'sequenceAnalyser/redux/actions/sequenceAnalyserActions';
import type { RemovedSequence } from 'sequenceAnalyser/utilities/sequenceAnalyser.types';
import { getSequenceLabel } from 'sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser';

export type InsertPositionValue = 'before' | 'after';

export interface ComponentProps {
  alignedSequences: SequenceAlign[];
  sequences: Sequence[];
  open: boolean;
  onOpen: (open: boolean) => void;
  onAlignedSequencesChange: HandleAlignedSequencesChange;
  removedSequences: RemovedSequence[];
}

// Sort removed sequences by the initial way they are sorted when loading the session
const sortRemovedSequencesById = (
  sequences: Sequence[], removedSequences: RemovedSequence[],
) => removedSequences.slice()
  .sort((a, b) => (
    (
      sequences.findIndex((s) => s.id === a.sequence.id)
      - sequences.findIndex((s) => s.id === b.sequence.id)
    )
  ));

export function InsertRemovedSequencesModal({
  open, onOpen, sequences, onAlignedSequencesChange, removedSequences, alignedSequences,
}: ComponentProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const dispatch = useDispatch<AppDispatch>();

  const sortedRemovedSequences = sortRemovedSequencesById(sequences, removedSequences);

  const hasSelectedAll = selected.length === sortedRemovedSequences.length;

  const handleSelectAll = () => {
    if (hasSelectedAll) {
      setSelected([]);
    } else {
      setSelected(sortedRemovedSequences.map((s, i) => i.toString()));
    }
  };

  return (
    <SimpleModal
      title={I18n.SequenceAnalyser.InsertRemovedSequencesModal.title}
      open={open}
      onClose={() => {
        onOpen(false);
      }}
      content={(
        <Box mt={1}>
          <ScrollableModalContent>
            <>
              {sortedRemovedSequences.length > 1 && (
                <InputCheckbox label="All" checked={hasSelectedAll} onChange={() => handleSelectAll()} />
              )}
              <InputCheckboxGroup
                values={selected}
                options={sortedRemovedSequences
                  .map((removedSequence, i) => ({
                    value: i.toString(),
                    label: getSequenceLabel(
                      sequences.find((sequence) => sequence.id === removedSequence.sequence.id) as Sequence,
                      [],
                    ),
                  }))}
                onChange={(newValue) => setSelected(arrayToggle(selected, newValue))}
              />
            </>
          </ScrollableModalContent>

          <ModalActions
            applyText={I18n.SequenceAnalyser.InsertRemovedSequencesModal.apply}
            disableApply={selected.length === 0}
            onApply={() => {
              const selectedRemovedSequences: RemovedSequence[] = sortRemovedSequencesById(
                sequences,
                selected.map((s) => sortedRemovedSequences[Number(s)]),
              );

              const updatedAlignedSequences = [
                ...alignedSequences,
                ...selectedRemovedSequences.map((rs) => rs.sequence),
              ];

              onAlignedSequencesChange(updatedAlignedSequences);

              dispatch(removedSequencesInserted(selectedRemovedSequences));

              // Close modal
              onOpen(false);
            }}
          />
        </Box>
      )}
    />
  );
}

export default InsertRemovedSequencesModal;
